/**
 * /coffee-commune — Sample re-org homepage for The Coffee Commune.
 *
 * The user is presenting this to Coffee Commune. The recommendation is to
 * split their multi-facet business into subdomains; this single landing
 * page is the unified "front door" that summarises every entity.
 *
 * Style: matches /platform (sales) — cream/earth tones, honey accent,
 * the same .sp-section / .sp-container rhythm so we feel familiar.
 */
import { useState } from "react";
import {
  Coffee, Flame, GraduationCap, Calendar, Package,
  ShoppingBag, Users, MapPin, ArrowRight, ChevronDown,
} from "lucide-react";
import "./sales/SalesPage.css";
import "./CoffeeCommunePage.css";
import QuickReserve from "./QuickReserve";

const ENTITIES = [
  {
    id: "cafe",
    icon: Coffee, accent: "#C75B3D",
    name: "The Cafe",
    tagline: "Daily coffee · brunch · community",
    suggestedSubdomain: "cafe.coffeecommune.com.au",
    hero: "https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=1200&q=80",
    description: "Your daily-driver coffee bar — espresso, brews, fresh food, indoor + outdoor seating. The walk-in heart of the brand.",
    submenu: [
      "Menu", "Brunch", "Coffee List", "Book a Table", "Hours & Location", "Gift Vouchers",
    ],
  },
  {
    id: "roastery",
    icon: Flame, accent: "#7B341E",
    name: "The Roastery",
    tagline: "Single-origin · house blends · subscriptions",
    suggestedSubdomain: "roastery.coffeecommune.com.au",
    hero: "https://images.unsplash.com/photo-1442975631115-c4f7b05b8a2c?w=1200&q=80",
    description: "Behind-the-scenes roasting & cupping. The story of every bean from farmer to portafilter — green-bean traceability, roast profiles, freshness dates.",
    submenu: [
      "Single Origins", "House Blends", "Roast Schedule", "Cupping Sessions", "Subscriptions",
    ],
  },
  {
    id: "wholesale",
    icon: Package, accent: "#8B4513",
    name: "Wholesale",
    tagline: "Restaurant & office accounts · branded blends",
    suggestedSubdomain: "wholesale.coffeecommune.com.au",
    hero: "https://images.unsplash.com/photo-1611854779393-1b2da9d400fe?w=1200&q=80",
    description: "B2B coffee program — bean supply, equipment lease, machine servicing, branded blends for cafes and offices. Account-managed, ordered online.",
    submenu: [
      "Become a Stockist", "Equipment", "Servicing", "Custom Blends", "Account Login",
    ],
  },
  {
    id: "academy",
    icon: GraduationCap, accent: "#B45309",
    name: "Coffee Academy",
    tagline: "Barista training · home-brewing · latte art",
    suggestedSubdomain: "academy.coffeecommune.com.au",
    hero: "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=1200&q=80",
    description: "Half-day to multi-week curriculum for new baristas, cafe owners, and home enthusiasts. SCA-aligned modules, in-person workshops, gift courses.",
    submenu: [
      "Public Workshops", "Barista Certification", "Latte Art", "Home Brewing", "Gift a Course", "Corporate Training",
    ],
  },
  {
    id: "events",
    icon: Calendar, accent: "#92400E",
    name: "Events & Venue Hire",
    tagline: "Functions · launches · brand activations",
    suggestedSubdomain: "events.coffeecommune.com.au",
    hero: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1200&q=80",
    description: "Hire the space — coffee bar activations, product launches, intimate dinners, photography & film shoots. Capacity, layouts, food & coffee packages.",
    submenu: [
      "Spaces & Capacity", "Catering Packages", "Hire Rates", "Past Events", "Enquire",
    ],
  },
  {
    id: "shop",
    icon: ShoppingBag, accent: "#A16207",
    name: "Online Shop",
    tagline: "Beans · brew gear · subscriptions",
    suggestedSubdomain: "shop.coffeecommune.com.au",
    hero: "https://images.unsplash.com/photo-1509042239860-f550ce710b93?w=1200&q=80",
    description: "Direct-to-consumer e-commerce. Whole bean, ground-to-order, brew gear, glassware, branded merchandise. Auto-renew subscriptions for regulars.",
    submenu: [
      "Whole Bean", "Subscriptions", "Brew Gear", "Merch", "Gift Cards", "My Account",
    ],
  },
  {
    id: "community",
    icon: Users, accent: "#854D0E",
    name: "Community",
    tagline: "Industry network · events · jobs board",
    suggestedSubdomain: "community.coffeecommune.com.au",
    hero: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=1200&q=80",
    description: "The reason it's called \"Commune\" — Brisbane's coffee-industry hub. Member directory, industry events, jobs board, supplier introductions.",
    submenu: [
      "Membership", "Events Calendar", "Jobs Board", "Member Directory", "Newsletter",
    ],
  },
];

export default function CoffeeCommunePage() {
  const [openMenu, setOpenMenu] = useState(null);

  return (
    <div className="sp-root cc-root" data-testid="coffee-commune-page">
      {/* ─── Top nav with entities + submenus ───────────────────────── */}
      <header className="cc-nav">
        <div className="sp-container cc-nav__inner">
          <a href="#top" className="cc-nav__brand">
            <Coffee className="cc-nav__brand-mark" />
            <span><strong>Coffee</strong> Commune</span>
          </a>
          <nav className="cc-nav__entities" aria-label="entities">
            {ENTITIES.map((e) => (
              <div
                key={e.id}
                className="cc-nav__entity"
                onMouseEnter={() => setOpenMenu(e.id)}
                onMouseLeave={() => setOpenMenu(null)}
                data-testid={`cc-nav-${e.id}`}
              >
                <a href={`#${e.id}`} className="cc-nav__link">
                  {e.name}
                  <ChevronDown className="cc-nav__chev" />
                </a>
                {openMenu === e.id && (
                  <div className="cc-nav__dropdown" data-testid={`cc-nav-dropdown-${e.id}`}>
                    {e.submenu.map((s) => (
                      <a key={s} href={`#${e.id}`} className="cc-nav__dd-item">{s}</a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
          <a href="#reserve" className="sp-btn sp-btn--primary cc-nav__cta" data-testid="cc-nav-reserve">Book a table</a>
        </div>
      </header>

      {/* ─── Hero ──────────────────────────────────────────────────── */}
      <section className="sp-section cc-hero" id="top">
        <div className="sp-container cc-hero__inner">
          <div className="cc-hero__copy">
            <div className="sp-eyebrow cc-hero__eyebrow">A re-imagined homepage</div>
            <h1 className="cc-hero__title">
              One door.<br />
              <span className="cc-hero__title-accent">Seven worlds inside.</span>
            </h1>
            <p className="cc-hero__lead">
              Coffee Commune is more than a cafe — it's a roastery, an academy,
              a wholesale operation, an event space, an online shop, and a
              community hub. Here's a clean, navigable front door that lets
              every visitor find <em>their</em> Coffee Commune in under three seconds.
            </p>
            <div className="cc-hero__ctas">
              <a href="#entities" className="sp-btn sp-btn--primary" data-testid="cc-hero-cta-explore">Explore the seven</a>
              <a href="#subdomains" className="sp-btn sp-btn--ghost" data-testid="cc-hero-cta-strategy">The subdomain strategy</a>
            </div>
          </div>
          <div className="cc-hero__mosaic">
            {ENTITIES.slice(0, 4).map((e) => (
              <div key={e.id} className="cc-hero__tile" style={{ backgroundImage: `url(${e.hero})` }}>
                <div className="cc-hero__tile-veil" style={{ background: `linear-gradient(180deg, transparent 40%, ${e.accent} 130%)` }} />
                <div className="cc-hero__tile-name">{e.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Entity cards ──────────────────────────────────────────── */}
      <section className="sp-section sp-section--cream cc-entities" id="entities">
        <div className="sp-container">
          <div className="sp-head-row">
            <div>
              <div className="sp-eyebrow">The seven entities</div>
              <h2 className="sp-h">A focused page for each side of the business</h2>
              <p className="sp-lead">Customers, students, wholesale buyers, event clients and online shoppers all behave differently. Each entity gets its own world — but they share one identity, one navigation, one checkout. Discover-able from this single landing.</p>
            </div>
          </div>

          <div className="cc-grid" data-testid="cc-entity-grid">
            {ENTITIES.map((e) => {
              const Icon = e.icon;
              return (
                <a
                  key={e.id}
                  href={`#${e.id}`}
                  id={e.id}
                  className="cc-card"
                  style={{ "--accent": e.accent }}
                  data-testid={`cc-card-${e.id}`}
                >
                  <div className="cc-card__media" style={{ backgroundImage: `url(${e.hero})` }}>
                    <div className="cc-card__media-veil" />
                    <div className="cc-card__icon"><Icon /></div>
                  </div>
                  <div className="cc-card__body">
                    <div className="cc-card__name">{e.name}</div>
                    <div className="cc-card__tagline">{e.tagline}</div>
                    <p className="cc-card__desc">{e.description}</p>
                    <div className="cc-card__submenu">
                      {e.submenu.slice(0, 5).map((s) => (
                        <span key={s} className="cc-card__pill">{s}</span>
                      ))}
                    </div>
                    <div className="cc-card__foot">
                      <span className="cc-card__sub">{e.suggestedSubdomain}</span>
                      <ArrowRight className="cc-card__arrow" />
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Book a Table — QuickReserve widget ────────────────────── */}
      <section className="sp-section cc-reserve" id="reserve">
        <div className="sp-container" style={{ display: "grid", gridTemplateColumns: "1.05fr 0.95fr", gap: 56, alignItems: "center" }}>
          <div>
            <div className="sp-eyebrow">Book a table</div>
            <h2 className="sp-h">A flat white at <span style={{ color: "#C75B3D" }}>The Cafe</span> — locked in, in 30 seconds.</h2>
            <p className="sp-lead">
              Pick your date, party size, and arrive-by time. The booking
              goes straight into Coffee Commune's system <em>and</em> your
              member profile — so the next time you tap "Plan my journey",
              we'll work backwards from your booking and tell you exactly
              when to leave home, which bus to catch, and which stop to
              get off at. Powered by HustlePlate.
            </p>
            <ul style={{ listStyle: "none", padding: 0, margin: "18px 0 0", color: "#444", fontSize: 14, lineHeight: 1.9 }}>
              <li>✓ <strong>No Fresha fee.</strong> No third-party booking commission.</li>
              <li>✓ <strong>SMS confirmation.</strong> 10-minute hold past your time.</li>
              <li>✓ <strong>One profile.</strong> Bookings sync to your HustlePlate member account.</li>
              <li>✓ <strong>Smart arrival.</strong> Future TransLink calculator already wired in.</li>
            </ul>
          </div>
          <div>
            <QuickReserve
              venueSlug="coffee-commune"
              accent="#C75B3D"
              title="Book a table at The Cafe"
            />
          </div>
        </div>
      </section>

      {/* ─── Subdomain strategy section ────────────────────────────── */}
      <section className="sp-section cc-strategy" id="subdomains">
        <div className="sp-container">
          <div className="sp-head-row">
            <div>
              <div className="sp-eyebrow">The recommendation</div>
              <h2 className="sp-h">Split each entity into its own subdomain — and unify them here</h2>
              <p className="sp-lead">Right now everything fights for the same homepage. Customers looking for a flat white land on a wall of wholesale forms. Wholesale buyers wade through a brunch menu. A clean subdomain structure lets each audience get a focused experience, while this landing acts as the brand-level switchboard.</p>
            </div>
          </div>

          <div className="cc-strategy__chart" data-testid="cc-strategy-chart">
            <div className="cc-strategy__node cc-strategy__root">
              <strong>coffeecommune.com.au</strong>
              <span>(this page · the switchboard)</span>
            </div>
            <div className="cc-strategy__branches">
              {ENTITIES.map((e) => (
                <div key={e.id} className="cc-strategy__node" style={{ "--accent": e.accent }}>
                  <e.icon className="cc-strategy__icon" />
                  <strong>{e.suggestedSubdomain}</strong>
                  <span>{e.name}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="cc-benefits">
            <Benefit
              title="SEO clarity"
              body="Each subdomain ranks for its own keyword cluster — 'coffee roastery Brisbane', 'barista course Brisbane', 'wholesale coffee suppliers QLD' — without diluting the main site."
              testid="cc-benefit-seo"
            />
            <Benefit
              title="Audience focus"
              body="A wholesale buyer arriving from a Google search hits a clean wholesale page in under one second — no scrolling past the cafe menu or workshop signups."
              testid="cc-benefit-audience"
            />
            <Benefit
              title="Independent analytics"
              body="Each subdomain's traffic, conversions and revenue tracked separately — you can see exactly which entity is your strongest growth lever month-on-month."
              testid="cc-benefit-analytics"
            />
            <Benefit
              title="Team ownership"
              body="The roastery team owns the roastery subdomain. The events team owns events. No more cross-team approvals to update a single page."
              testid="cc-benefit-team"
            />
            <Benefit
              title="Unified brand"
              body="Shared header, identical theme, one login — visitors barely notice the URL change. They just feel that everything is in the right place."
              testid="cc-benefit-brand"
            />
            <Benefit
              title="Future-proof"
              body="When you add an 8th entity (e.g. a sister cafe, a coffee-machine repair arm, a coffee-tour business), you just spin up an 8th subdomain — the brand grows without breaking the homepage."
              testid="cc-benefit-future"
            />
          </div>
        </div>
      </section>

      {/* ─── CTA ───────────────────────────────────────────────────── */}
      <section className="sp-section cc-cta" id="enquire">
        <div className="sp-container cc-cta__inner">
          <div className="cc-cta__copy">
            <div className="sp-eyebrow cc-cta__eyebrow">Let's talk</div>
            <h2 className="cc-cta__h">Want this exact homepage live for Coffee Commune?</h2>
            <p className="cc-cta__lead">
              HustlePlate ships every page above — plus the seven subdomains
              behind it — as a single platform: cafe POS, online shop,
              subscriptions, events bookings, academy LMS, wholesale portal,
              member directory. One backend, one bill, one team to call.
            </p>
            <div className="cc-cta__row">
              <MapPin className="cc-cta__pin" />
              <span>Built for Brisbane independents · 30-day pilot · Australian-owned</span>
            </div>
            <a href="https://wa.me/61400000000?text=Hi%20HustlePlate%2C%20I%20want%20to%20talk%20about%20the%20Coffee%20Commune%20re-org%20homepage" className="sp-btn sp-btn--primary cc-cta__btn" data-testid="cc-cta-whatsapp">
              Book the demo
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function Benefit({ title, body, testid }) {
  return (
    <div className="cc-benefit" data-testid={testid}>
      <div className="cc-benefit__title">{title}</div>
      <p className="cc-benefit__body">{body}</p>
    </div>
  );
}
